import { Userpilot } from 'userpilot';
import { userpilotDomain } from 'components/helper/helper';
import { startUserpilotBadgeRelocator } from './userpilotLabelBeacon';

const LOCALHOST_HOSTS = new Set(['localhost', '3.14.196.144']);

type UserpilotTraits = Record<string, unknown>;

function isUserpilotEnabled(): boolean {
  const hostname = window.location?.hostname;
  return Boolean(hostname && !LOCALHOST_HOSTS.has(hostname));
}

/** Remove corrupted Userpilot localStorage entries that break JSON.parse inside the SDK. */
function scrubCorruptedUserpilotStorage(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key || !key.toLowerCase().includes('userpilot')) continue;
      const value = localStorage.getItem(key);
      if (value === 'undefined' || value === 'null') {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore storage access errors
  }
}

function sanitizeTraits(traits: UserpilotTraits): Record<string, string | number | boolean | string[]> {
  const out: Record<string, string | number | boolean | string[]> = {};
  for (const [key, value] of Object.entries(traits)) {
    if (value === undefined || value === null || value === 'undefined') continue;
    if (Array.isArray(value)) {
      const cleaned = value
        .filter((item) => item != null && item !== 'undefined')
        .map(String);
      if (cleaned.length > 0) out[key] = cleaned;
      continue;
    }
    if (typeof value === 'object') continue;
    out[key] = value as string | number | boolean;
  }
  return out;
}

export function initializeUserpilot(): void {
  startUserpilotBadgeRelocator();
  if (!isUserpilotEnabled()) return;
  scrubCorruptedUserpilotStorage();
  try {
    Userpilot.initialize(process.env.REACT_APP_USERPILOTOKEN || 'STG-NX-9ce94dc6');
  } catch (err) {
    console.warn('[Userpilot] initialize failed', err);
  }
}

export function identifyUserpilot(
  userId: string | number | null | undefined,
  userRole: string | null | undefined,
  traits: UserpilotTraits = {},
): void {
  if (!isUserpilotEnabled()) return;
  if (userId == null || userId === '' || userId === 'undefined') return;

  const role = userRole ?? '';
  try {
    Userpilot.identify(String(userId) + userpilotDomain(role), sanitizeTraits(traits));
  } catch (err) {
    console.warn('[Userpilot] identify failed', err);
  }
}
